import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Checkbox, Link, Typography } from "@mui/material";

import { VerificationIcon } from "../../../../assets/icons/MUI/NetworkIcons";
import { useUserDataFormatter } from "../../hooks/useUserDataFormatter";
import { UserCardProps } from "../../types/props/userCardProps";

import { UserActions } from "./UserActions";

import {
  avatarStyle,
  checkboxStyle,
  verificationIconStyle,
  getCardWrapperStyle,
  getCardContentStyle,
  getFullNameStyle,
  fullNameWrapperStyle,
  getTypographyStyles,
} from "./styles";

export { default as UserCardLoader } from "./UserCardLoader";

const UserCard = ({
  user,
  variant = "list",
  showCheckbox = false,
  isSelected = false,
  onSelect,
  isDisabled = false,
  hasActions = false,
}: UserCardProps) => {
  const { t, i18n } = useTranslation("user", { keyPrefix: "userCard" });
  const navigate = useNavigate();

  const currentLanguage = i18n.language;

  const {
    fullName,
    firstTwoChr,
    countryName,
    countryMedia,
    isVerified,
    media,
    role,
    isFollowing,
    profileLink,
    mutualFollowersCount,
    playerPositionShort,
    friendsCount,
  } = useUserDataFormatter(user);

  const [openCreateMessage, setOpenCreateMessage] = useState<boolean>(false);

  const typographyStyles = getTypographyStyles({ variant });

  return (
    <Box sx={getCardWrapperStyle({ variant })}>
      {showCheckbox && (
        <Checkbox
          size="small"
          sx={checkboxStyle[variant]}
          onClick={() => onSelect?.(user)}
          disabled={isDisabled}
          checked={isSelected}
        />
      )}

      <Box sx={{ position: "relative" }}>
        <Avatar sx={avatarStyle[variant]} src={media}>
          {firstTwoChr}
        </Avatar>
        {isVerified && <VerificationIcon sx={verificationIconStyle[variant]} />}
      </Box>

      <Box sx={getCardContentStyle({ variant })}>
        <Box sx={fullNameWrapperStyle({ variant })}>
          <Link sx={getFullNameStyle({ variant })} onClick={() => navigate(profileLink)}>
            {fullName}
          </Link>
          <img src={countryMedia} height="9px" width="12px" alt={countryName} />
        </Box>

        <Typography sx={typographyStyles.roleClub}>
          {role}
          {playerPositionShort ? ` . (${playerPositionShort})` : ""}
        </Typography>

        <Typography sx={typographyStyles.mutualFollowersAndFriendsCount}>
          {Number(mutualFollowersCount) > 0 && t("mutualFriends", { count: Number(mutualFollowersCount) })}
          {Number(friendsCount) > 0 && t("friendWith", { count: Number(friendsCount) })}
        </Typography>

        {variant !== "grid" && countryName && (
          <Typography sx={typographyStyles.countryName} title={countryName}>
            {countryName}
          </Typography>
        )}

        {hasActions && (
          <UserActions
            user={user}
            variant={variant}
            isFollowing={isFollowing}
            openCreateMessage={openCreateMessage}
            setOpenCreateMessage={setOpenCreateMessage}
            currentLanguage={currentLanguage}
          />
        )}
      </Box>
    </Box>
  );
};

export default UserCard;
